import { useId, useMemo, useRef } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { useTranslation } from 'utils/translation';

interface QRCodeProps {
  value: string;
  bgColor?: string;
  fgColor?: string;
  level?: 'L' | 'M' | 'Q' | 'H';
  className?: string;
  onClick?(): void;
  showLabel?: boolean;
  customLabel?: string;
  paddingX?: number;
  paddingY?: number;
}

const QR_SIZE = 100;
const LABEL_SPACING = 8;
const LABEL_FONT_SIZE = 14;
const MIN_LABEL_FONT_SIZE = 8;
const LABEL_FONT_FAMILY = 'Arial, sans-serif';

function measureTextWidth(
  text: string,
  fontSize: number,
  fontWeight: string,
): number {
  if (typeof document === 'undefined') {
    return text.length * fontSize * 0.55;
  }

  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  if (!context) {
    return text.length * fontSize * 0.55;
  }

  context.font = `${fontWeight} ${fontSize}px ${LABEL_FONT_FAMILY}`;
  return context.measureText(text).width;
}

function getLabelLayout(textLabel: string) {
  const textWidth = measureTextWidth(textLabel, LABEL_FONT_SIZE, 'bold');

  if (textWidth <= QR_SIZE) {
    return {
      labelFontSize: LABEL_FONT_SIZE,
      contentWidth: QR_SIZE,
      qrOffsetX: 0,
    };
  }

  const scaledFontSize = LABEL_FONT_SIZE * (QR_SIZE / textWidth);
  if (scaledFontSize >= MIN_LABEL_FONT_SIZE) {
    return {
      labelFontSize: scaledFontSize,
      contentWidth: QR_SIZE,
      qrOffsetX: 0,
    };
  }

  const widthAtMinFont = measureTextWidth(
    textLabel,
    MIN_LABEL_FONT_SIZE,
    'bold',
  );
  const contentWidth = Math.max(QR_SIZE, widthAtMinFont);

  return {
    labelFontSize: MIN_LABEL_FONT_SIZE,
    contentWidth,
    qrOffsetX: (contentWidth - QR_SIZE) / 2,
  };
}

export default function QRCode({
  value,
  bgColor = '#ffffff',
  fgColor = '#000000',
  level = 'L',
  className,
  onClick,
  showLabel = true,
  customLabel = '',
  paddingX = 0,
  paddingY = 0,
}: QRCodeProps) {
  const id = useId();
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const textLabel = customLabel || value;
  const labelLayout = useMemo(
    () => (showLabel ? getLabelLayout(textLabel) : null),
    [showLabel, textLabel],
  );
  const labelFontSize = labelLayout?.labelFontSize ?? LABEL_FONT_SIZE;
  const contentWidth = labelLayout?.contentWidth ?? QR_SIZE;
  const qrOffsetX = labelLayout?.qrOffsetX ?? 0;
  const labelHeight = LABEL_SPACING + labelFontSize;
  const contentHeight = showLabel ? QR_SIZE + labelHeight : QR_SIZE;
  const totalWidth = contentWidth + paddingX * 2;
  const totalHeight = contentHeight + paddingY * 2;

  const downloadSvg = () => {
    const svg = containerRef.current?.querySelector('svg');
    if (!svg) return;

    const serializer = new XMLSerializer();
    const svgString = serializer.serializeToString(svg);
    const blob = new Blob([svgString], { type: 'image/svg+xml' });
    const url = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.download = `${textLabel}.svg`;
    link.click();

    URL.revokeObjectURL(url);
  };

  return (
    <div
      className="cursor-pointer"
      ref={containerRef}
      title={t('Click to download')}
      onClick={downloadSvg}
    >
      <svg
        id={id}
        width={totalWidth}
        height={totalHeight}
        viewBox={`0 0 ${totalWidth} ${totalHeight}`}
        xmlns="http://www.w3.org/2000/svg"
        className={className}
      >
        <rect width={totalWidth} height={totalHeight} fill={bgColor} />
        <g transform={`translate(${paddingX + qrOffsetX}, ${paddingY})`}>
          <QRCodeSVG
            onClick={onClick}
            value={value}
            size={QR_SIZE}
            bgColor={bgColor}
            fgColor={fgColor}
            level={level}
          />
        </g>
        {showLabel && (
          <text
            x={totalWidth / 2}
            y={paddingY + QR_SIZE + labelHeight}
            textAnchor="middle"
            fontFamily={LABEL_FONT_FAMILY}
            fontSize={labelFontSize}
            fontWeight="bold"
            fill={fgColor}
          >
            {textLabel}
          </text>
        )}
      </svg>
    </div>
  );
}
